import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNil } from "lodash";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import fpSimulationStepService from "@plannel/services/fp/fp-simulation-step-service";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { codeComboCellEditorParams, useCommonCodeValues } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// 공통코드 그룹 FP_EXEC_TYPE의 두 코드(02_fp_seed_comn_code.sql). 실행 유형이 PROCEDURE일 때만
// 실행 대상(execTarget)이 의미를 갖는다 — old onCellEdited/validFunc와 동일한 분기 기준.
const EXEC_TP_PLAN = "FP_EXEC_PLAN";
const EXEC_TP_PROCEDURE = "FP_EXEC_PROCEDURE";

// 좌측 절차 목록 패널 폭 — old ClickableListPanel(Details width 220px)과 동일.
const STEP_LIST_WIDTH = 220;
// old 헤더 영역 높이(52px)를 좌/우 패널 모두에 맞춰 두 패널의 첫 줄이 같은 baseline에 오게 한다.
const PANEL_HEADER_HEIGHT = 52;

const isBlank = v => isNil(v) || v === "";

// DataState.setDataState는 값 변경 후 "마지막 포커스 셀"을 되살리고, 그 셀이 방금 바뀐 셀과 다르면
// startEditingCell까지 호출한다(DataState.js:62-70). 코드가 다른 컬럼 값을 바꾸면 사용자가 건드리지도
// 않은 셀이 편집 모드로 열리므로, 포커스를 비운 채 값을 바꾸고 원래 셀로 되돌린다
// (clearFocusedCell 이후엔 getFocusedCell()이 null이라 DataState가 포커스 블록 자체를 건너뛴다).
const setCellValueQuietly = (gridApi, node, colId, value) => {
  const focusedCell = gridApi.getFocusedCell();

  gridApi.clearFocusedCell();
  node.setDataValue(colId, value);

  if (focusedCell) {
    gridApi.setFocusedCell(focusedCell.rowIndex, focusedCell.column);
  }
};

const SimulationStep = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");

  // 좌측 절차 목록(tb_fp_plan_step) / 우측 상세(tb_fp_plan_step_seq)의 마스터-디테일 구성.
  const [stepList, setStepList] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [stepSeqs, setStepSeqs] = useState([]);
  // 아직 저장 전인 신규 절차 모드 — 저장 시 stepCd를 null로 보내면 서버가 채번한다.
  const [isNewStep, setIsNewStep] = useState(false);
  const [stepNm, setStepNm] = useState("");
  const [newStepTick, setNewStepTick] = useState(0);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const latestStepReqRef = useRef(0);
  const latestSeqReqRef = useRef(0);
  // 삭제는 서버 호출이 아니라 저장 시 changes.delete로 함께 보내는 지연 삭제(old와 동일).
  const deleteDataRef = useRef([]);

  const execTpValuesRef = useCommonCodeValues("FP_EXEC_TYPE");

  const defaultGridMemo = useMemo(() => FpGridSetting({ title, viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  // TODO(MIG-T2): [simulationstep/SimulationStep] 컬럼 필터 미이관.
  //   old: RealGrid 기본 필터 UI(그리드 헤더 필터) 사용.
  //   사유: 컬럼 필터는 Tier2(1차 유보) 표준 항목 — FP 다른 화면과 동일하게 미적용.
  const columnDefs = [
    // id는 axios-bigint의 useNativeBigInt로 파싱돼 typeof "bigint" — AG Grid 타입 추론이
    // number/string/boolean/date 어디에도 안 걸려 "object"로 폴백해 경고를 내므로 추론을 끈다.
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // stepCd는 좌측에서 선택한 절차의 코드 — 화면엔 안 보이고 저장 payload로만 나간다(old도 headerText " ").
    { headerName: "FP_PLAN_STEP", field: "stepCd", colId: "stepCd", hide: true, type: ["nonEditableColumn"] },
    // 표시 순서는 stepSeq가 유일한 기준이다. 사용자가 다른 컬럼으로 정렬하면 순서 조정/행 추가의
    // 번호 재부여 로직과 화면 순서가 어긋나므로, 화면 전체에서 정렬을 끄고(defaultColDef.sortable=false)
    // 여기에만 코드로 sort를 건다. AG Grid 35는 sortable=false여도 colDef.sort는 그대로 적용한다.
    { headerName: "seq", field: "stepSeq", colId: "stepSeq", type: ["nonEditableColumn", "centerAligned"], width: 80, sort: "asc" },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", cellClass: "stringType", width: 320,
      // old editor {type:"text", maxLength:100} 대응(tb_fp_plan_step_seq.desc_txt도 100자).
      cellEditorParams: { valuePlaceholder: "", maxLength: 100 } },
    { headerName: "FP_EXEC_TP_CD", field: "execTpCd", colId: "execTpCd", cellClass: "stringType", width: 220,
      type: ["centerAligned"], cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      // 셀 표시는 value 그대로, 드롭다운 목록만 "value - label"로 (§8 공통코드 콤보 공통 헬퍼).
      cellEditorParams: () => codeComboCellEditorParams("FP_EXEC_TYPE", execTpValuesRef, t) },
    // 실행 유형이 PROCEDURE인 행에서만 편집 가능 — old styleCallback의 editable 토글과 동일.
    // TODO(MIG-T2): [simulationstep/SimulationStep] 편집 가능 셀 시각 표시(배경색) 미이관 — FP 전 화면 공통.
    //   old: 공용 BaseGrid가 editable 컬럼에 배경 #FFFFD2를 자동 부여하고, 행별 조건부는 화면
    //        styleCallback이 editable-text-column(#F8FBFF)/text-column으로 전환했다(old SimulationStep.jsx:39-45).
    //   사유: new엔 편집 가능 셀 채색 관례가 없고 FP 4개 완료 화면도 동일하게 없다. 화면별 cellStyle
    //        하드코딩 대신 FpGridSetting에 일괄 적용해야 하며(공용 수정), 색상도 미정이다.
    //        편집 차단/허용 기능 자체는 아래 editable 콜백으로 정상 동작한다.
    { headerName: "FP_EXEC_TARGET", field: "execTarget", colId: "execTarget", cellClass: "stringType", width: 220,
      editable: params => params.data?.execTpCd === EXEC_TP_PROCEDURE },
    // 수정 정보(audit) 그룹 — FP master 화면과 동일한 FP_COL_AUDIT 그룹 패턴 재사용.
    // createUser/modifyUser는 PlanStepSeqQueryRepository의 z_user 2-join으로 채워진다(백엔드 리빌드 필요).
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  // 절차 상세 조회. stepCd 단위 조회라 좌측 선택이 바뀔 때마다 호출된다.
  const retrieveStepSeqs = (stepCd) => {
    if (!planScope) return;

    const reqId = ++latestSeqReqRef.current;
    deleteDataRef.current = [];

    gridRef.current?.api?.setGridOption("loading", true);

    fpSimulationStepService.getAll({ planScope, stepCd })
      .then(response => {
        if (reqId !== latestSeqReqRef.current) return;
        const results = response.data ?? [];
        setStepSeqs(results);
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (reqId !== latestSeqReqRef.current) return;
        const reduxState = reduxUtil.createObjectState(viewName, { planScope });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        // old loadData 말미의 gridView.checkAll(false) 대응 — 이전 선택이 삭제 대상으로 남지 않게 한다.
        gridRef.current?.api?.deselectAll();
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestSeqReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  // TODO(MIG-T2): [simulationstep/SimulationStep] 절차 전환/신규 생성 시 미저장 변경 경고(MSG_5142) 미이관.
  //   old: setStep/createStep/handleCancel이 getViewIsUpdated(viewId)로 dirty를 확인해 MSG_5142 확인을 받은
  //   뒤에만 gridView.cancel() 후 전환했다.
  //   사유: FP 전 화면 공통(Route/Resource/Stock/Wip와 동일 계열) 유보 — 화면별로 다르게 가지 않는다.
  const selectStep = (step) => {
    setIsNewStep(false);
    setCurrentStep(step ?? null);
    setStepNm(step?.stepNm ?? "");

    if (step?.stepCd) {
      retrieveStepSeqs(step.stepCd);
      return;
    }

    // 선택할 절차가 없으면(목록이 비었을 때) 진행 중인 상세 응답을 무효화하고 그리드를 비운다.
    latestSeqReqRef.current += 1;
    deleteDataRef.current = [];
    setStepSeqs([]);
    DataState.initialize(gridRef.current?.api);
  };

  // 좌측 절차 목록 조회. 저장 직후에는 방금 저장한 절차(preferredStepCd)를 다시 선택한다.
  const retrievePlanSteps = (preferredStepCd) => {
    if (!planScope) return;

    const reqId = ++latestStepReqRef.current;

    // viewId/subId는 old와 동일하게 보내지만 서버에서는 사실상 무시된다 —
    // LookupQueryParameter.exampleMode가 planScope만 보고 결정되기 때문(LookupQueryParameter.java:250).
    fpCommonService.getPlanSteps({ viewId: viewName, subId: "", planScope })
      .then(response => {
        if (reqId !== latestStepReqRef.current) return;
        const results = response.data ?? [];
        setStepList(results);
        selectStep(results.find(step => step.stepCd === preferredStepCd) ?? results[0] ?? null);
      })
      .catch(e => {
        debug(e);
      });
  };

  const onGridReady = params => {
    // getStateData는 gridApi[stateRows]가 없으면 예외가 나므로 조회보다 먼저 초기화한다.
    DataState.initialize(params.api);
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  // TODO(MIG-T2): [simulationstep/SimulationStep] 붙여넣기 도메인 검사(checkDomainOnly) 미이관.
  //   old: gridView.pasteOptions.checkDomainOnly = true — 드롭다운 컬럼에 목록에 없는 값이 붙여넣기로 들어오는 것을 차단.
  //   사유: 범위선택/클립보드는 Tier2(1차 유보) 표준 항목. 직접 편집은 agRichSelectCellEditor가 목록값만 허용한다.
  //
  // 공용 DefaultGridSetting.onCellValueChanged(gridPaste + DataState.setDataState)를 먼저 위임한 뒤
  // 화면 고유 분기를 얹는다 — 위임이 먼저여야 DataState가 진짜 원본을 origin으로 잡는다(Wip.js와 동일 순서).
  const onCellValueChanged = params => {
    defaultGridMemo.onCellValueChanged(params);

    if (params.column.getColId() !== "execTpCd") return;

    const { api: gridApi, node } = params;

    if (params.newValue === EXEC_TP_PLAN) {
      // PLAN에는 실행 대상이 없다 — old onCellEdited와 동일하게 비운다.
      if (isBlank(node.data.execTarget)) return;
      setCellValueQuietly(gridApi, node, "execTarget", null);
    } else if (params.newValue === EXEC_TP_PROCEDURE) {
      // PROCEDURE로 되돌리면 비우기 전 값을 복원한다. old는 RealGrid getUpdatedCells()의 oldValue를 읽었는데,
      // 그 자리에 대응하는 것이 DataState의 UPDATED_ORIGIN 스냅샷이다(신규 행은 스냅샷이 없어 복원 대상 아님 —
      // old는 이 경우 빈 배열을 인덱싱해 예외가 났다).
      const origin = DataState.getUpdateOrigin(gridApi)?.[Number(node.id)];
      const restored = origin?.execTarget;
      if (isBlank(restored) || restored === node.data.execTarget) return;
      setCellValueQuietly(gridApi, node, "execTarget", restored);
    }
  };

  // 행 추가 — old addRow와 동일하게 "포커스된 행 다음"에 넣고 뒤쪽 stepSeq를 1씩 밀어준다.
  const insertRow = () => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    gridApi.stopEditing();

    // 삽입 위치는 **포커스된 행 다음**이다(old GridAddRowButton의 getCurrent().dataRow 기준 —
    // 마지막 행이면 beginAppendRow, 아니면 beginInsertRow(rowIndex+1)). 체크박스는 삭제 대상을
    // 고르는 용도라 삽입 위치와 무관하다. 포커스가 없으면(빈 그리드/신규 절차) 맨 끝에 붙는다.
    // getFocusedCell()은 clearFocusedCell() 전에 읽어야 한다.
    const rowCount = gridApi.getDisplayedRowCount();
    const focusedCell = gridApi.getFocusedCell();
    const insertIndex = focusedCell ? focusedCell.rowIndex + 1 : rowCount;

    gridApi.clearFocusedCell();

    const prevNode = insertIndex > 0 ? gridApi.getDisplayedRowAtIndex(insertIndex - 1) : null;
    const newStepSeq = prevNode ? Number(prevNode.data.stepSeq ?? 0) + 1 : 1;

    // 뒤쪽 행부터 역순으로 밀어야 중간에 stepSeq가 겹치지 않는다(겹치면 정렬 순서가 흔들린다).
    for (let index = rowCount - 1; index >= insertIndex; index -= 1) {
      gridApi.getDisplayedRowAtIndex(index)?.setDataValue("stepSeq", newStepSeq + (index - insertIndex) + 1);
    }

    const newRow = {
      id: null,
      planScope,
      // 신규 절차는 stepCd가 아직 없다(저장 시 서버가 채번). 기존 절차면 그 절차에 속한 행이다.
      stepCd: isNewStep ? null : (currentStep?.stepCd ?? null),
      stepSeq: newStepSeq,
      descTxt: "",
      execTpCd: "",
      execTarget: ""
    };

    const createdNode = GridUtils.addRow(gridApi, newRow, insertIndex);
    DataState.setDataState(createdNode);
    gridApi.refreshClientSideRowModel("sort");

    if (!isNil(createdNode.rowIndex)) {
      gridApi.ensureIndexVisible(createdNode.rowIndex);
    }
  };

  // 순서 조정 — 이웃 행과 stepSeq를 맞바꾼다. 행 노드를 먼저 잡아두면 중간에 재정렬이 일어나도
  // 인덱스가 흔들리지 않는다. applyTransaction(remove/add)은 노드가 새로 생겨 DataState의 상태 추적
  // (노드 id 기반)이 끊기므로 쓰지 않는다.
  const moveRow = (offset) => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    gridApi.stopEditing();

    const focusedCell = gridApi.getFocusedCell();
    if (isNil(focusedCell)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("FP_MSG_SELECT_MOVE_ROW") });
      return;
    }

    const targetIndex = focusedCell.rowIndex + offset;
    if (targetIndex < 0 || targetIndex >= gridApi.getDisplayedRowCount()) return;

    const currentNode = gridApi.getDisplayedRowAtIndex(focusedCell.rowIndex);
    const targetNode = gridApi.getDisplayedRowAtIndex(targetIndex);
    if (!currentNode || !targetNode) return;

    const currentSeq = currentNode.data.stepSeq;
    const targetSeq = targetNode.data.stepSeq;

    gridApi.clearFocusedCell();
    currentNode.setDataValue("stepSeq", targetSeq);
    targetNode.setDataValue("stepSeq", currentSeq);
    gridApi.refreshClientSideRowModel("sort");

    gridApi.ensureIndexVisible(targetIndex);
    gridApi.setFocusedCell(targetIndex, focusedCell.column);
  };

  // 행 삭제 — 서버 호출 없이 저장 시 함께 보낼 목록에 쌓아두고 그리드에서만 뺀다(old와 동일).
  const removeRows = () => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    gridApi.stopEditing();

    const selectedNodes = gridApi.getSelectedNodes()
      .slice()
      .sort((a, b) => a.rowIndex - b.rowIndex);

    if (selectedNodes.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("FP_MSG_NO_ROW_CHECKED") });
      return;
    }

    const firstIndex = selectedNodes[0].rowIndex;
    const firstStepSeq = Number(selectedNodes[0].data.stepSeq ?? 1);

    // 미저장 신규 행은 서버에 없으니 삭제 목록에서 뺀다(old의 getRowState !== 'created' 조건과 동일).
    // old는 모듈 전역 deleteData에 **덮어써서** 두 번 나눠 지우면 앞 삭제가 유실됐다 — 여기서는 누적한다.
    const staged = selectedNodes.filter(node => !isNil(node.data.id)).map(node => node.data);
    deleteDataRef.current = [...deleteDataRef.current, ...staged];

    gridApi.clearFocusedCell();
    DataState.deleteState(gridApi, selectedNodes);

    // 삭제로 생긴 stepSeq 구멍 메우기 — old deleteRow와 동일하게 삭제 시작 위치부터 다시 매긴다.
    const rowCount = gridApi.getDisplayedRowCount();
    for (let index = firstIndex, stepSeq = firstStepSeq; index < rowCount; index += 1, stepSeq += 1) {
      gridApi.getDisplayedRowAtIndex(index)?.setDataValue("stepSeq", stepSeq);
    }
    gridApi.refreshClientSideRowModel("sort");
  };

  const saveStep = () => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) {
      setProgressOn(false);
      return;
    }

    gridApi.stopEditing();

    if (isBlank(stepNm)) {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: t("FP_PLAN_STEP_NM") }) });
      return;
    }

    // TODO(MIG-T2): [simulationstep/SimulationStep] 저장 검증 실패 시 셀 단위 오류 목록 표시 미이관.
    //   old: getInvalidCells()로 "행번호/컬럼명/사유"를 줄 단위로 모아 MSG_VALIDATE_ERROR_SAVE_DATA 뒤에 붙여 보여줬다.
    //   사유: new에 셀 단위 검증 결과 수집 API가 없다. 저장 차단과 안내(FP_VALIDATION_FAIL)는 동일하게 동작한다.
    //
    // old validateCells(null, false)는 변경된 행이 아니라 **전체 행**을 검사한다 — 동일하게 맞춘다.
    const invalidRow = GridUtils.getAllData(gridApi).find(row =>
      isBlank(row.descTxt)
      || isBlank(row.execTpCd)
      || (row.execTpCd === EXEC_TP_PROCEDURE && isBlank(row.execTarget)));

    if (invalidRow) {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_VALIDATION_FAIL") });
      return;
    }

    const createData = DataState.getStateData(gridApi, "created");
    const updateData = DataState.getStateData(gridApi, "updated");
    const removeData = deleteDataRef.current;
    const hasStepNmChanged = !isNewStep && stepNm !== (currentStep?.stepNm ?? "");

    if (createData.length === 0 && updateData.length === 0 && removeData.length === 0 && !hasStepNmChanged) {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    fpSimulationStepService.save({
      planScope,
      stepCd: isNewStep ? null : (currentStep?.stepCd ?? null),
      stepNm,
      update: updateData,
      create: createData,
      remove: removeData
    })
      .then(response => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        // 신규 절차는 서버가 stepCd(FP_STEP_<yyyyMMddHHmmss>)를 채번해 newPlanStepCd로 돌려준다 —
        // 목록을 다시 읽고 그 절차를 선택한다.
        retrievePlanSteps(response.data?.data?.newPlanStepCd ?? currentStep?.stepCd);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        setProgressOn(false);
      });
  };

  // 신규 절차 — 좌측 선택을 풀고 그리드를 비운 뒤 빈 행 하나로 시작한다(old createStep).
  const createStep = () => {
    latestSeqReqRef.current += 1;

    setIsNewStep(true);
    setCurrentStep(null);
    setStepNm("");
    deleteDataRef.current = [];
    setStepSeqs([]);
    DataState.initialize(gridRef.current?.api);

    // rowData가 []로 반영된 **뒤**에 첫 행을 넣어야 한다(old beginAppendRow + addRow).
    // 상태값 대신 카운터를 쓰는 이유: 조건 분기 없이 "신규 생성 클릭"만 트리거로 쓰기 위해서다.
    setNewStepTick(tick => tick + 1);
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setProgressOn(true);
      saveStep();
    }
  };

  useEffect(() => {
    if (!gridLoading) return;
    retrievePlanSteps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, planScope]);

  useEffect(() => {
    if (newStepTick === 0) return;
    insertRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newStepTick]);

  return (
    <>
      {/* TODO(MIG-T2): [simulationstep/SimulationStep] 조회영역 없음 표시(NO_SEARCHAREA) 미이관.
          old: useEffect에서 setViewInfo(activeViewId, 'NO_SEARCHAREA', true)로 전역 뷰 정보에 등록.
          사유: new에는 화면별 뷰 정보 레지스트리라는 개념 자체가 없다(Tier3). */}
      <FilterContainer hasIcons={false}>
        <PlanScope value={planScope} onChange={setPlanScope} />
      </FilterContainer>
      <Box sx={{ display: "flex", gap: 1.5, flex: 1, minHeight: 0, mt: 1, mb: 1 }}>
        <Box sx={{ width: STEP_LIST_WIDTH, flex: "0 0 auto", display: "flex", flexDirection: "column", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ height: PANEL_HEADER_HEIGHT, flex: "0 0 auto", display: "flex", alignItems: "center", px: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <Button fullWidth size="small" variant="outlined" startIcon={<AddIcon />} onClick={createStep} sx={{ minHeight: 32, lineHeight: 1.2 }}>
              {t("FP_SIMUL_STEP_CREATION")}
            </Button>
          </Box>
          <List sx={{ py: 2, flex: 1, minHeight: 0, overflow: "auto" }}>
            {stepList.map(step => (
              <ListItem key={step.stepCd} disablePadding>
                <ListItemButton
                  selected={!isNewStep && currentStep?.stepCd === step.stepCd}
                  onClick={() => selectStep(step)}
                  sx={{
                    minHeight: 32,
                    px: "14px",
                    py: 1,
                    borderLeft: 3,
                    borderColor: !isNewStep && currentStep?.stepCd === step.stepCd ? "primary.main" : "transparent"
                  }}
                >
                  <ListItemText primary={step.stepNm} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden", border: isNewStep ? 2 : 1, borderColor: isNewStep ? "primary.main" : "divider", borderRadius: 1 }}>
          <Box sx={{ height: PANEL_HEADER_HEIGHT, flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1, px: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              size="small"
              label={t("FP_PLAN_STEP_NM")}
              placeholder={t("FP_MSG_ENTER_DESCRIP")}
              value={stepNm}
              onChange={event => setStepNm(event.target.value)}
              sx={{ width: 600, maxWidth: "60%" }}
            />
            {isNewStep && <FiberNewOutlinedIcon color="primary" />}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
              {isNewStep && (
                <Button size="small" onClick={() => selectStep(stepList[0] ?? null)}>
                  {t("cancel")}
                </Button>
              )}
              <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
            </Box>
          </Box>
          <Box sx={{ p: 1.5, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Tooltip title={t("FP_CHANGE_SEQ")}>
                <IconButton aria-label="moveRowUp" color="info" size="small" onClick={() => moveRow(-1)}>
                  <ArrowUpwardIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("FP_CHANGE_SEQ")}>
                <IconButton aria-label="moveRowDown" color="info" size="small" onClick={() => moveRow(1)}>
                  <ArrowDownwardIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ pl: "0.3rem", color: "text.secondary" }}>
                {`(${t("FP_MSG_DESCRIP_MOVE_ROW")})`}
              </Typography>
              <Box sx={{ ml: "auto" }}>
                <AddButton onClick={insertRow} />
                <RemoveButton onClick={removeRows} />
              </Box>
            </Box>
            <Box id="fp-simulation-step-grid" className="ag-theme-balham" sx={{ flex: 1, minHeight: 0 }}>
              {/* TODO(MIG-T2): [simulationstep/SimulationStep] 체크박스 컬럼 헤더 텍스트(FP_SELECT_DELETE) 미이관.
                  old: setCheckBar({visible:true, showAll:false, headText:transLangKey('FP_SELECT_DELETE'), width:40, syncHeadCheck:true}).
                  사유: 장식성 헤더 라벨(Tier2). 체크 선택/전체선택 동작 자체는 rowSelection으로 동일하게 이관됐다. */}
              <AgGridReact
                ref={gridRef}
                rowData={stepSeqs}
                {...defaultGridMemo}
                defaultColDef={{ ...defaultGridMemo.defaultColDef, sortable: false }}
                rowSelection={{
                  ...defaultGridMemo.rowSelection,
                  headerCheckbox: true,
                  checkboxes: true,
                  // 체크박스 = 삭제 대상, 포커스 셀 = 순서 조정 대상으로 분리한다(old checkBar / getCurrent()와 동일).
                  // 클릭 선택을 켜두면 순서를 바꾸려고 행을 클릭하는 것만으로 삭제 대상이 된다.
                  enableClickSelection: false
                }}
                onGridReady={onGridReady}
                onCellValueChanged={onCellValueChanged}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(SimulationStep);
